// src/components/EditTemplate.jsx
import React, { useState, useEffect } from 'react';
import './FormBuilder.css';
import config from '../config';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'image', label: 'Images' },
];

const EditTemplate = ({ templateId, onTemplateUpdated, onCancel }) => {
  const [templateName, setTemplateName] = useState('');
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    }
  }, [templateId]);

  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const fetchTemplate = async () => {
    setIsLoading(true);
    setError('');

    try {
      const csrfToken = getCookie('csrftoken');

      const response = await fetch(`${config.API_BASE_URL}/api/form-templates/${templateId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.status}`);
      }

      const data = await response.json();
      // console.log('Fetched template:', data);

      setTemplateName(data.name);

      const transformedSections = data.sections.map(section => ({
        id: section.id.toString(),
        name: section.name,
        fields: section.fields.map(field => ({
          id: field.id.toString(),
          label: field.label,
          field_type: field.field_type,
          required: field.required,
          validation_criteria: field.validation_criteria || {},
          options: field.options || [],
        }))
      }));

      setSections(transformedSections);

    } catch (error) {
      // console.error('Error fetching template:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const addSection = () => {
    const newSection = {
      id: Date.now().toString(),
      name: `Section ${sections.length + 1}`,
      fields: []
    };
    setSections([...sections, newSection]);
    setError('');
  };

  const updateSectionName = (sectionId, name) => {
    setSections(sections.map(section =>
      section.id === sectionId ? { ...section, name } : section
    ));
  };

  const addField = (sectionId) => {
    const newField = {
      id: Date.now().toString(),
      label: 'New Field',
      field_type: 'text',
      required: false,
      validation_criteria: {},
      options: []
    };

    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, fields: [...section.fields, newField] }
        : section
    ));
  };

  const updateField = (sectionId, fieldId, updates) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? {
          ...section,
          fields: section.fields.map(field =>
            field.id === fieldId ? { ...field, ...updates } : field
          )
        }
        : section
    ));
  };

  const removeSection = (sectionId) => {
    setSections(sections.filter(section => section.id !== sectionId));
  };

  const removeField = (sectionId, fieldId) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? {
          ...section,
          fields: section.fields.filter(field => field.id !== fieldId)
        }
        : section
    ));
  };

  const validateTemplate = () => {
    if (!templateName.trim()) {
      setError('Please enter a template name');
      return false;
    }

    if (sections.length === 0) {
      setError('Please add at least one section');
      return false;
    }

    const sectionsWithoutNames = sections.filter(s => !s.name.trim());
    if (sectionsWithoutNames.length > 0) {
      setError('All sections must have names');
      return false;
    }

    const emptySections = sections.filter(section => section.fields.length === 0);
    if (emptySections.length > 0) {
      setError('All sections must have at least one field');
      return false;
    }

    for (const section of sections) {
      const fieldsWithoutLabels = section.fields.filter(f => !f.label.trim());
      if (fieldsWithoutLabels.length > 0) {
        setError('All fields must have labels');
        return false;
      }
    }

    for (const section of sections) {
      for (const field of section.fields) {
        if (['select', 'radio', 'checkbox'].includes(field.field_type) &&
          (!field.options || field.options.length === 0)) {
          setError(`Field "${field.label}" must have at least one option`);
          return false;
        }
      }
    }

    return true;
  };

  const updateTemplate = async () => {
    setError('');
    setSuccessMessage('');

    if (!validateTemplate()) {
      return;
    }

    setIsSaving(true);

    try {
      const csrfToken = getCookie('csrftoken');

      const templateData = {
        name: templateName,
        sections_data: sections.map(section => ({
          name: section.name,
          fields: section.fields.map(field => {
            const fieldData = {
              label: field.label,
              field_type: field.field_type,
              required: field.required,
              validation_criteria: field.validation_criteria || {},
            };

            if (['select', 'radio', 'checkbox'].includes(field.field_type)) {
              fieldData.options = field.options || [];
            }

            return fieldData;
          })
        }))
      };

      // console.log('Updating template:', templateData);

      const response = await fetch(`${config.API_BASE_URL}/api/form-templates/${templateId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(templateData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }

      setSuccessMessage('Template updated successfully!');

      // Notify parent and go back after 2 seconds
      setTimeout(() => {
        onTemplateUpdated();
      }, 2000);

    } catch (error) {
      // console.error('Error updating template:', error);
      setError(error.message || 'Failed to update template');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="form-builder-container loading">
        <div className="loading-spinner"></div>
        <p>Loading template...</p>
      </div>
    );
  }

  return (
    <div className="form-builder-container">
      <div className="builder-header">
        <button onClick={onCancel} className="back-button" disabled={isSaving}>
          Cancel
        </button>
        <h1 className="builder-title">Edit Template</h1>
      </div>

      {error && (
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          <div className="error-content">
            <strong>Error:</strong>
            <p>{error}</p>
          </div>
          <button className="error-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-alert">
          <span className="success-icon">✓</span>
          <span>{successMessage}</span>
        </div>
      )}

      <div className="template-name-section">
        <label htmlFor="templateName" className="field-label">
          Template Name <span className="required-star">*</span>
        </label>
        <input
          id="templateName"
          type="text"
          placeholder="Enter template name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          className="template-name-input"
          disabled={isSaving}
        />
      </div>

      <div className="builder-actions">
        <button
          onClick={addSection}
          className="btn-add-section"
          disabled={isSaving}
        >
          <span className="btn-icon">+</span>
          Add Section
        </button>

        <button
          onClick={updateTemplate}
          disabled={isSaving || !templateName || sections.length === 0}
          className="btn-save-template"
        >
          {isSaving ? (
            <>
              <span className="loading-spinner-small"></span>
              Updating...
            </>
          ) : (
            <>
              <span className="btn-icon">💾</span>
              Update Template
            </>
          )}
        </button>
      </div>

      {sections.length === 0 ? (
        <div className="empty-state-builder">
          <div className="empty-icon">📋</div>
          <h3>No sections added yet</h3>
          <p>Click "Add Section" to start building your form</p>
        </div>
      ) : (
        sections.map((section) => (
          <div key={section.id} className="section-card-builder">
            <div className="section-header-builder">
              <div className="section-title-input">
                <input
                  type="text"
                  value={section.name}
                  onChange={(e) => updateSectionName(section.id, e.target.value)}
                  className="section-name-input"
                  placeholder="Section name"
                  disabled={isSaving}
                />
              </div>

              <button
                onClick={() => removeSection(section.id)}
                className="btn-remove-section"
                disabled={isSaving}
                title="Remove Section"
              >
                🗑️
              </button>
            </div>

            <button
              onClick={() => addField(section.id)}
              className="btn-add-field"
              disabled={isSaving}
            >
              <span className="btn-icon">+</span>
              Add Field
            </button>

            {section.fields.length === 0 ? (
              <div className="empty-fields-message">
                No fields in this section. Click "Add Field" to get started.
              </div>
            ) : (
              section.fields.map((field) => (
                <div key={field.id} className="field-card-builder">
                  <div className="field-header">
                    <span className="field-type-badge">
                      {FIELD_TYPES.find(t => t.value === field.field_type)?.label || 'Text'}
                    </span>
                    <button
                      onClick={() => removeField(section.id, field.id)}
                      className="btn-remove-field"
                      disabled={isSaving}
                      title="Remove Field"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="field-inputs">
                    <div className="field-label-input">
                      <label className="field-sub-label">Field Label *</label>
                      <input
                        type="text"
                        placeholder="e.g., Full Name"
                        value={field.label}
                        onChange={(e) => updateField(section.id, field.id, { label: e.target.value })}
                        className="text-input"
                        disabled={isSaving}
                      />
                    </div>

                    <div className="field-type-select">
                      <label className="field-sub-label">Field Type *</label>
                      <select
                        value={field.field_type}
                        onChange={(e) => updateField(section.id, field.id, { field_type: e.target.value })}
                        className="select-input"
                        disabled={isSaving}
                      >
                        {FIELD_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="field-required">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(section.id, field.id, { required: e.target.checked })}
                        disabled={isSaving}
                      />
                      This field is required
                    </label>
                  </div>

                  {['select', 'radio', 'checkbox'].includes(field.field_type) && (
                    <div className="field-options">
                      <label className="field-sub-label">Options (one per line) *</label>
                      <textarea
                        value={field.options?.join('\n') || ''}
                        onChange={(e) => updateField(section.id, field.id, {
                          options: e.target.value.split('\n').filter(opt => opt.trim())
                        })}
                        className="options-textarea"
                        rows={4}
                        placeholder="Option 1&#10;Option 2&#10;Option 3"
                        disabled={isSaving}
                      />
                      <p className="options-help">Enter each option on a new line</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ))
      )}
    </div>

  );
};

export default EditTemplate;